import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles, Trash2, Megaphone } from "lucide-react";
import { toast } from "sonner";

const KINDS = [
  { id: "ad", label: "Ad Copy" },
  { id: "social", label: "Social Posts" },
  { id: "email", label: "Email Campaign" },
  { id: "landing", label: "Landing Page" },
];

export default function Marketing() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({
    kind: "ad", product: "", audience: "", tone: "professional", key_points: "",
  });
  const [busy, setBusy] = useState(false);
  const [active, setActive] = useState(null);

  const load = () => api.get("/marketing").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.product.trim() || !form.audience.trim())
      return toast.error("Product and audience required");
    setBusy(true);
    try {
      const { data } = await api.post("/marketing/generate", form);
      setActive(data);
      toast.success("Content generated");
      load();
    } catch {
      toast.error("Generation failed");
    } finally {
      setBusy(false);
    }
  };

  const del = async (id) => {
    await api.delete(`/marketing/${id}`);
    toast.success("Deleted");
    if (active?.id === id) setActive(null);
    load();
  };

  return (
    <div className="p-8 space-y-6" data-testid="marketing-page">
      <div>
        <div className="overline text-accent">/ MARKETING STUDIO</div>
        <h1 className="font-display text-4xl font-bold tracking-tight mt-2">AI Content Studio</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Ads, social, emails, landing pages — on-brand in seconds.
        </p>
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        <form onSubmit={submit} className="lg:col-span-2 border border-border bg-card p-6 space-y-4 h-fit" data-testid="marketing-form">
          <div>
            <Label>Content Type</Label>
            <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
              <SelectTrigger className="rounded-sm mt-1.5" data-testid="mkt-kind"><SelectValue /></SelectTrigger>
              <SelectContent>
                {KINDS.map((k) => <SelectItem key={k.id} value={k.id}>{k.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Product / Service</Label>
            <Input required data-testid="mkt-product" className="rounded-sm mt-1.5"
              placeholder="e.g., Industrial IoT platform"
              value={form.product} onChange={(e) => setForm({ ...form, product: e.target.value })} />
          </div>
          <div>
            <Label>Target Audience</Label>
            <Input required data-testid="mkt-audience" className="rounded-sm mt-1.5"
              placeholder="e.g., Plant managers at mid-size manufacturers"
              value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })} />
          </div>
          <div>
            <Label>Tone</Label>
            <Select value={form.tone} onValueChange={(v) => setForm({ ...form, tone: v })}>
              <SelectTrigger className="rounded-sm mt-1.5" data-testid="mkt-tone"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["professional", "bold", "friendly", "technical", "witty"].map((t) => (
                  <SelectItem key={t} value={t}>{t}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Key points</Label>
            <Textarea rows={3} data-testid="mkt-keypoints" className="rounded-sm mt-1.5"
              placeholder="Value props, offers, benefits…"
              value={form.key_points} onChange={(e) => setForm({ ...form, key_points: e.target.value })} />
          </div>
          <Button type="submit" disabled={busy}
            data-testid="mkt-generate-btn"
            className="w-full rounded-sm bg-accent hover:bg-accent/90 text-white">
            <Sparkles className="w-4 h-4 mr-2" /> {busy ? "Generating…" : "Generate with AI"}
          </Button>
        </form>

        <div className="lg:col-span-3 space-y-4">
          {active ? (
            <div className="border border-border bg-card p-6" data-testid="mkt-active">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <div className="overline text-accent">{active.kind}</div>
                  <div className="font-display text-xl font-bold mt-1">{active.product}</div>
                </div>
                <span className="text-xs font-mono-plex text-muted-foreground uppercase tracking-widest">
                  {active.audience}
                </span>
              </div>
              <div className="prose prose-sm max-w-none whitespace-pre-wrap font-mono-plex text-sm leading-relaxed">
                {active.content}
              </div>
            </div>
          ) : (
            <div className="border border-dashed border-border p-16 text-center h-full flex flex-col items-center justify-center">
              <Megaphone className="w-8 h-8 text-muted-foreground" />
              <p className="mt-4 font-display text-lg">No content yet</p>
              <p className="text-sm text-muted-foreground mt-1">Fill the form and click Generate.</p>
            </div>
          )}

          {items.length > 0 && (
            <div className="border border-border bg-card">
              <div className="p-4 border-b border-border overline text-muted-foreground">Recent</div>
              <ul>
                {items.slice(0, 6).map((it) => (
                  <li key={it.id} className="p-4 border-b border-border last:border-b-0 flex items-center justify-between hover:bg-secondary/50" data-testid={`mkt-item-${it.id}`}>
                    <button className="text-left flex-1" onClick={() => setActive(it)}>
                      <div className="text-xs overline text-accent">{it.kind}</div>
                      <div className="font-medium text-sm">{it.product}</div>
                      <div className="text-xs text-muted-foreground">{it.audience}</div>
                    </button>
                    <Button variant="ghost" size="sm" onClick={() => del(it.id)} data-testid={`mkt-delete-${it.id}`}>
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
